import { useEffect, useRef } from 'react';

/**
 * Modal-dialog accessibility: Escape-to-close, initial focus, and a focus
 * trap that cycles Tab/Shift+Tab inside the container.
 *
 * Usage:
 *   const ref = useDialogA11y(onClose);
 *   <form ref={ref} ...>...</form>
 *
 * Caveats:
 *   - For conditionally-MOUNTED dialogs (`open ? <Dialog/> : null`) call
 *     with the default `enabled`. For self-gating components that render
 *     `null` while closed (the scanners), pass `open` as the second arg —
 *     hooks must run unconditionally, so call it ABOVE the early return
 *     and let `enabled` control when the listeners/trap attach.
 *   - If there's an element with `data-autofocus` it wins for initial
 *     focus; otherwise we pick the first non-readonly <input>, falling
 *     back to the first focusable.
 *   - Escape always fires onClose, even if focus is outside the dialog —
 *     that matches what users expect from a modal overlay.
 */
export function useDialogA11y<T extends HTMLElement>(onClose: () => void, enabled = true) {
  const ref = useRef<T>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const node = ref.current;
    if (!node) return;
    const focusables = collectFocusable(node);
    if (focusables.length === 0) return;

    // Pick initial focus: explicit data-autofocus, then first writable
    // input, then the first focusable element (often the close button).
    const explicit = node.querySelector<HTMLElement>('[data-autofocus]');
    const firstInput = focusables.find(
      (el) => el.tagName === 'INPUT' && !(el as HTMLInputElement).readOnly,
    );
    const initial = explicit ?? firstInput ?? focusables[0];
    // Defer to next frame so the browser has painted the modal first.
    requestAnimationFrame(() => initial?.focus());

    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = collectFocusable(node);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !node.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    node.addEventListener('keydown', trap);
    return () => node.removeEventListener('keydown', trap);
  }, [enabled]);

  return ref;
}

function collectFocusable(root: HTMLElement): HTMLElement[] {
  const sel =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll<HTMLElement>(sel)).filter(
    (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null,
  );
}
